import { isEmailValid } from "ui/components/TextField/EmailTextField/emailUtils";
import * as yup from "yup";

export const IGNORE_ERROR = "IGNORE";

export const EMAIL_EXISTS_ERROR = "Korisnik s ovom email adresom već postoji";

export const USERNAME_EXISTS_ERROR = "Korisnik s ovim korisničkim imenom već postoji";

/** Poruka za zauzeto korisničko ime; naziv tenanta dodaje se kad ga poslužitelj vrati. */
export const getUserNameExistsError = (tenantName: string | null): string => {
  if (!tenantName) return `${USERNAME_EXISTS_ERROR}.`;

  // Naziv često završava točkom (d.o.o.), pa se druga ne dodaje.
  const suffix = tenantName.endsWith(".") ? "" : ".";
  return `${USERNAME_EXISTS_ERROR} na tenantu ${tenantName}${suffix}`;
};

// Dopušteni znakovi isti su kao u Identity postavkama na backendu (AllowedUserNameCharacters).
const USERNAME_RULE = yup
  .string()
  .trim()
  .required('Molimo Vas da unesete korisničko ime')
  .min(3, 'Korisničko ime mora imati najmanje 3 znaka')
  .matches(
    /^[a-zA-Z0-9._@+-]+$/,
    'Korisničko ime smije sadržavati samo slova bez dijakritika, brojeve i znakove . _ - @ +'
  );

/** Poruka za zauzet email; naziv tenanta dodaje se kad ga poslužitelj vrati. */
export const getEmailExistsError = (tenantName: string | null): string => {
  if (!tenantName) return `${EMAIL_EXISTS_ERROR}.`;

  // Naziv često završava točkom (d.o.o.), pa se druga ne dodaje.
  const suffix = tenantName.endsWith(".") ? "" : ".";
  return `${EMAIL_EXISTS_ERROR} na tenantu ${tenantName}${suffix}`;
};

// Pravila lozinke usklađena s Identity postavkama na backendu (Program.cs).
// Prazna vrijednost prolazi; obaveznost se određuje posebno za unos i uređivanje.
const withPasswordPolicy = (schema: yup.StringSchema<string | null | undefined>) =>
  schema
    .test('password-length', 'Lozinka mora imati najmanje 8 znakova', (value) => !value || value.length >= 8)
    .test('password-lowercase', 'Lozinka mora sadržavati barem jedno malo slovo', (value) => !value || /[a-z]/.test(value))
    .test('password-uppercase', 'Lozinka mora sadržavati barem jedno veliko slovo', (value) => !value || /[A-Z]/.test(value))
    .test('password-digit', 'Lozinka mora sadržavati barem jedan broj', (value) => !value || /\d/.test(value))
    .test('password-special', 'Lozinka mora sadržavati barem jedan poseban znak', (value) => !value || /[^a-zA-Z0-9]/.test(value));

export const USER_CREATE_FORM_SCHEMA = yup.object().shape({
  firstName: yup.string().required('Molimo Vas da unesete ime'),
  lastName: yup.string().required('Molimo Vas da unesete prezime'),
  phoneNumber: yup
    .string()
    .nullable()
    .when('mobilePhoneNumber', {
      is: (val: string | null | undefined) => !val || val.trim() === '',
      then: (schema) => schema.required('Molimo Vas da unesete telefonski broj ili broj mobitela'),
      otherwise: (schema) => schema.notRequired(),
    }),
  mobilePhoneNumber: yup
    .string()
    .nullable()
    .when('phoneNumber', {
      is: (val: string | null | undefined) => !val || val.trim() === '',
      then: (schema) => schema.required('Molimo Vas da unesete broj mobitela ili telefonski broj'),
      otherwise: (schema) => schema.notRequired(),
    }),
  email: yup
    .string()
    .required('Molimo Vas da unesete email adresu')
    .test(
      "valid-email",
      "Neispravna email adresa",
      (value) => {
        if (!value) return true;
        return isEmailValid(value);
      }
    ),
  userName: USERNAME_RULE,
  password: withPasswordPolicy(yup.string().required('Molimo Vas da unesete korisničku lozinku')),
  confirmPassword: yup
    .string()
    .required('Molimo Vas da ponovite lozinku')
    .oneOf([yup.ref('password')], 'Lozinke se ne podudaraju'),
  roles: yup
    .array()
    .of(yup.string())
    .test(
      'at-least-one-role',
      'Molimo Vas da odaberete barem jednu rolu',
      (value) => Array.isArray(value) && value.length > 0
    ),
}, [['phoneNumber', 'mobilePhoneNumber']]);

export const USER_EDIT_FORM_SCHEMA = yup.object().shape({
  firstName: yup.string().required('Molimo Vas da unesete ime'),
  lastName: yup.string().required('Molimo Vas da unesete prezime'),
  phoneNumber: yup
    .string()
    .nullable()
    .when('mobilePhoneNumber', {
      is: (val: string | null | undefined) => !val || val.trim() === '',
      then: (schema) => schema.required('Molimo Vas da unesete telefonski broj ili broj mobitela'),
      otherwise: (schema) => schema.notRequired(),
    }),
  mobilePhoneNumber: yup
    .string()
    .nullable()
    .when('phoneNumber', {
      is: (val: string | null | undefined) => !val || val.trim() === '',
      then: (schema) => schema.required('Molimo Vas da unesete broj mobitela ili telefonski broj'),
      otherwise: (schema) => schema.notRequired(),
    }),
  email: yup
    .string()
    .required('Molimo Vas da unesete email adresu')
    .test(
      "valid-email",
      "Neispravna email adresa",
      (value) => {
        if (!value) return true;
        return isEmailValid(value);
      }
    ),
  userName: USERNAME_RULE,
  password: withPasswordPolicy(yup.string().nullable().notRequired()),
  confirmPassword: yup
    .string()
    .nullable()
    .when('password', {
      is: (val: string | null | undefined) => !!val && val.length > 0,
      then: (schema) =>
        schema
          .required('Molimo Vas da ponovite lozinku')
          .oneOf([yup.ref('password')], 'Lozinke se ne podudaraju'),
      otherwise: (schema) => schema.notRequired(),
    }),
  roles: yup
    .array()
    .of(yup.string())
    .test(
      'at-least-one-role',
      'Molimo Vas da odaberete barem jednu rolu',
      (value) => Array.isArray(value) && value.length > 0
    ),
}, [['phoneNumber', 'mobilePhoneNumber']]);


/*---------------------------------------------------------------------------------------
  PO-KORAK SHEME (navigacija "Naprijed")
-----------------------------------------------------------------------------------------*/

// Korak 1 — Osnovni podaci
export const USER_INFO_STEP_SCHEMA = yup.object().shape({
  firstName: yup.string().required('Molimo Vas da unesete ime'),
  lastName: yup.string().required('Molimo Vas da unesete prezime'),
});

// Zajednička pravila za rolu (korak 2)
const rolesRule = yup
  .array()
  .of(yup.string())
  .test(
    'at-least-one-role',
    'Molimo Vas da odaberete barem jednu rolu',
    (value) => Array.isArray(value) && value.length > 0
  );

// Korak 2 — Korisnički podaci (kreiranje: lozinka obavezna)
export const USER_ACCOUNT_STEP_CREATE_SCHEMA = yup.object().shape({
  userName: USERNAME_RULE,
  password: withPasswordPolicy(yup.string().required('Molimo Vas da unesete korisničku lozinku')),
  confirmPassword: yup
    .string()
    .required('Molimo Vas da ponovite lozinku')
    .oneOf([yup.ref('password')], 'Lozinke se ne podudaraju'),
  roles: rolesRule,
});

// Korak 2 — Korisnički podaci (uređivanje: lozinka opcionalna)
export const USER_ACCOUNT_STEP_EDIT_SCHEMA = yup.object().shape({
  userName: USERNAME_RULE,
  password: withPasswordPolicy(yup.string().nullable().notRequired()),
  confirmPassword: yup
    .string()
    .nullable()
    .when('password', {
      is: (val: string | null | undefined) => !!val && val.length > 0,
      then: (schema) =>
        schema
          .required('Molimo Vas da ponovite lozinku')
          .oneOf([yup.ref('password')], 'Lozinke se ne podudaraju'),
      otherwise: (schema) => schema.notRequired(),
    }),
  roles: rolesRule,
});

// Korak 3 — Kontakt (email obavezan; telefon ili mobitel — barem jedan)
export const USER_CONTACT_STEP_SCHEMA = yup.object().shape({
  email: yup
    .string()
    .required('Molimo Vas da unesete email adresu')
    .test('valid-email', 'Neispravna email adresa', (value) => {
      if (!value) return true;
      return isEmailValid(value);
    }),
  phoneNumber: yup
    .string()
    .nullable()
    .when('mobilePhoneNumber', {
      is: (val: string | null | undefined) => !val || val.trim() === '',
      then: (schema) =>
        schema.required('Molimo Vas da unesete telefonski broj ili broj mobitela'),
      otherwise: (schema) => schema.notRequired(),
    }),
  mobilePhoneNumber: yup
    .string()
    .nullable()
    .when('phoneNumber', {
      is: (val: string | null | undefined) => !val || val.trim() === '',
      then: (schema) =>
        schema.required('Molimo Vas da unesete broj mobitela ili telefonski broj'),
      otherwise: (schema) => schema.notRequired(),
    }),
}, [['phoneNumber', 'mobilePhoneNumber']]);
